import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import { H4 } from "orcs-design-system";

import { StyledTable } from "./MappingTable.styles";

const RootWrapper = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
`;

const TableContainer = styled.div`
  width: 0;
  min-width: 100%;
  height: 280px;
  overflow: auto;
  border: 1px solid ${themeGet("colors.greyLight")};
  border-radius: ${themeGet("radii.2")};
  margin-top: ${themeGet("space.s")};

  table {
    display: block;
    width: max-content;
    min-width: 100%;
  }

  thead,
  tbody {
    display: table;
    width: 100%;
    table-layout: auto;
  }
`;

const TableRow = styled.tr`
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};

  &:hover {
    background-color: ${(props) =>
      props.clickable ? themeGet("colors.greyLightest")(props) : "transparent"};
  }
`;

const TableCell = styled.td`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
  min-height: 32px;
  padding: ${themeGet("space.xs")};
  border: 1px solid ${themeGet("colors.greyLight")};
  box-sizing: border-box;
`;

const TableHeaderRow = styled(TableRow)`
  background-color: ${themeGet("colors.greyLightest")};
  font-weight: ${themeGet("fontWeights.2")};

  &:hover {
    background-color: ${themeGet("colors.greyLightest")};
  }
`;

/**
 * SimpleDataTable - A lightweight table component for displaying file preview data.
 * Renders tabular data with support for header row selection and horizontal scrolling.
 *
 * @param {boolean} loading - Whether data is being loaded
 * @param {Array<Array<any>>} data - 2D array of data to display
 * @param {function} setHeader - Function to call when a row is clicked (for header selection)
 * @param {boolean} canSetHeader - Whether rows should be clickable for header selection
 */
const SimpleDataTable = ({ loading, data, setHeader, canSetHeader }) => {
  if (loading || !data || data.length === 0) {
    return null;
  }

  // Find the maximum number of columns across all rows
  const maxColumns = Math.max(...data.map((row) => row.length));

  // Normalize all rows to have the same number of columns
  const normalizedData = data.map((row) => {
    const normalizedRow = [...row];
    // Pad with empty strings if row is shorter than maxColumns
    while (normalizedRow.length < maxColumns) {
      normalizedRow.push("");
    }
    return normalizedRow;
  });

  const handleRowClick = (rowData) => {
    if (canSetHeader && setHeader) {
      setHeader(rowData);
    }
  };

  return (
    <RootWrapper>
      <H4 mt="l" mb="s" fontWeight="bold">
        {canSetHeader ? "Please click the header row" : "File preview"}
      </H4>
      <TableContainer>
        <StyledTable>
          <tbody>
            {/* eslint-disable react/no-array-index-key */}
            {normalizedData.map((row, rowIndex) => {
              const isHeaderRow = rowIndex === 0;
              const RowComponent = isHeaderRow ? TableHeaderRow : TableRow;

              return (
                <RowComponent
                  key={`row-${rowIndex}`}
                  onClick={() => handleRowClick(data[rowIndex])}
                  clickable={canSetHeader && !isHeaderRow}
                >
                  {row.map((cell, cellIndex) => (
                    <TableCell key={`cell-${rowIndex}-${cellIndex}`}>
                      {cell !== null && cell !== undefined ? String(cell) : ""}
                    </TableCell>
                  ))}
                </RowComponent>
              );
            })}
            {/* eslint-enable react/no-array-index-key */}
          </tbody>
        </StyledTable>
      </TableContainer>
    </RootWrapper>
  );
};

SimpleDataTable.propTypes = {
  loading: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.array),
  setHeader: PropTypes.func,
  canSetHeader: PropTypes.bool,
};

export default SimpleDataTable;
